import { combineReducers } from 'redux';
import { omit } from 'lodash';
import managedPhaseListReducer from './phase_items';
import documents from './documents';
import related from './related_phases';

const initialState = {
  loading: false,
  loaded: false,
  errors: {},
  phone_numbers: [],
  surface_information: {},
  store: {},
  surface_type: {},
  events: [],
  current_tasks: [],
  estimatorsByWorkroom: [],
  installersForAssignment: [],
};

const getErrors = state => state.errors;
const getLoading = state => state.loading;
const getCurrentTasks = state => state.current_tasks;

const changeTask = (task, taskID, action) => {
  if (task.slug === taskID) {
    return { ...task, status: action };
  }
  return task;
};

const getEvents = (state, kind = null) => state.events.filter(e => !kind || e.kind === kind);
const getFlags = state => state.flags;

const removeRedundantProperties = phase => omit(phase, ['purchase_orders']);

const details = (state = initialState, { type, payload, meta }) => {
  switch (type) {
    case 'PHASE_RESOURCE_REQUEST':
      return {
        ...state,
        loading: true,
        loaded: false,
        ...(payload.optimisticState || {}),
      };
    case 'PHASE_RESOURCE_CREATE_REQUEST':
    case 'PHASE_RESOURCE_COPY_REQUEST':
    case 'PHASE_RESOURCE_UPDATE_REQUEST':
    case 'ADD_FLAG_TO_PHASE_REQUEST':
    case 'REMOVE_FLAG_FROM_PHASE_REQUEST':
    case 'ESTIMATOR_LIST_BY_WORKROOM_REQUEST':
    case 'INSTALLERS_FOR_ASSIGNMENT_REQUEST':
    case 'PHASE_APPOINTMENTS_REQUEST':
    case 'CANCEL_PHASE_REQUEST':
    case 'FETCH_CONTRACTOR_LIST_REQUEST':
    case 'VERIFY_PHASE_ASSESSMENT_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'UPDATE_PHASE_TASK_REQUEST':
      return {
        ...state,
        current_tasks: getCurrentTasks(state).map(task => changeTask(task, payload, 'pending')),
        loading: true,
      };
    case 'PHASE_RESOURCE_SUCCESS':
    case 'PHASE_RESOURCE_CREATE_SUCCESS':
    case 'PHASE_RESOURCE_COPY_SUCCESS':
    case 'PHASE_RESOURCE_UPDATE_SUCCESS':
    case 'OVERRIDE_PHASE_STATUS_SUCCESS':
    case 'UPDATE_PHASE_TASK_SUCCESS':
    case 'ADD_FLAG_TO_PHASE_SUCCESS':
    case 'REMOVE_FLAG_FROM_PHASE_SUCCESS':
    case 'ASSIGN_PHASE_ESTIMATOR_SUCCESS':
    case 'ASSIGN_PHASE_INSTALLER_SUCCESS':
    case 'APPROVE_LABOR_SHEET_SUCCESS':
    case 'CANCEL_PHASE_SUCCESS':
    case 'VERIFY_PHASE_ASSESSMENT_SUCCESS':
    case 'CREATE_RELATED_PHASE_SUCCESS':
    case 'SEND_QUICK_SCHEDULE_MESSAGE_SUCCESS':
      return {
        ...state,
        ...removeRedundantProperties(payload.phase),
        loading: false,
        loaded: true,
        errors: {},
      };
    case 'PHASE_RESOURCE_CLEAR_VALIDATION':
      return {
        ...state,
        errors: {},
      };
    case 'ADD_EVENT_TO_PHASE_SUCCESS':
      return {
        ...state,
        events: [payload.event, ...state.events],
        loading: false,
        errors: {},
      };
    case 'ESTIMATOR_LIST_BY_WORKROOM_SUCCESS':
      return {
        ...state,
        loading: false,
        estimatorsByWorkroom: payload.estimatorListByWorkroom,
      };
    case 'INSTALLERS_FOR_ASSIGNMENT_SUCCESS':
      return {
        ...state,
        loading: false,
        installersForAssignment: payload.installersForAssignment,
      };
    case 'FETCH_CONTRACTOR_LIST_SUCCESS':
      return {
        ...state,
        loading: false,
        contractors: payload.contractors,
      };
    case 'PHASE_APPOINTMENTS_SUCCESS':
      return {
        ...state,
        loading: false,
        appointments: payload.appointments,
      };
    case 'PHASE_APPOINTMENT_CREATE_SUCCESS':
      return {
        ...state,
        loading: false,
        errors: {},
        appointments: [...(state.appointments || []), payload.appointment],
        ...payload.phase,
      };
    case 'PHASE_APPOINTMENT_EDIT_SUCCESS': {
      // when editing a related phases appointment we don't want to overwrite the phase
      // we are currently viewing with the related phases details
      const phase = payload.phase.id === state.id ? payload.phase : {};
      return {
        ...state,
        loading: false,
        errors: {},
        appointments: [
          ...(state.appointments || []).filter(appt => appt.id !== payload.appointment.id),
          payload.appointment,
        ],
        ...phase,
      };
    }
    case 'PHASE_APPOINTMENT_DELETE_SUCCESS':
      return {
        ...state,
        loading: false,
        errors: {},
        appointments: state.appointments.filter(appt => appt.id !== payload.appointmentID),
        events: [payload.event, ...state.events],
      };
    case 'SURVEY_UPDATE_SUCCESS':
      return {
        ...state,
        ...removeRedundantProperties(payload.meta.phase),
      };
    case 'PHASE_RESOURCE_FAILURE':
    case 'PHASE_RESOURCE_CREATE_FAILURE':
    case 'PHASE_RESOURCE_COPY_FAILURE':
    case 'PHASE_RESOURCE_UPDATE_FAILURE':
    case 'ADD_EVENT_TO_PHASE_FAILURE':
    case 'OVERRIDE_PHASE_STATUS_FAILURE':
    case 'ADD_FLAG_TO_PHASE_FAILURE':
    case 'REMOVE_FLAG_FROM_PHASE_FAILURE':
    case 'ESTIMATOR_LIST_BY_WORKROOM_FAILURE':
    case 'INSTALLERS_FOR_ASSIGNMENT_FAILURE':
    case 'FETCH_CONTRACTOR_LIST_FAILURE':
    case 'PHASE_APPOINTMENTS_FAILURE':
    case 'ASSIGN_PHASE_ESTIMATOR_FAILURE':
    case 'ASSIGN_PHASE_INSTALLER_FAILURE':
    case 'CANCEL_PHASE_FAILURE':
    case 'VERIFY_PHASE_ASSESSMENT_FAILURE':
    case 'SEND_QUICK_SCHEDULE_MESSAGE_FAILURE':
      return {
        ...state,
        errors: payload.errors || {},
        loading: false,
      };
    case 'UPDATE_PHASE_TASK_FAILURE':
      return {
        ...state,
        errors: payload.errors || {},
        current_tasks: getCurrentTasks(state).map(task => changeTask(task, meta.taskID, 'incomplete')),
        loading: false,
      };
    case 'CREATE_PHASE_ITEMS_SUCCESS':
    case 'UPDATE_PHASE_ITEMS_SUCCESS':
    case 'DELETE_PHASE_ITEMS_SUCCESS':
    case 'REQUEST_PHASE_ITEMS_SUCCESS':
      if (!meta || !meta.phase) return state;
      return {
        ...state,
        ...meta.phase,
      };
    default:
      return state;
  }
};


const initialSurveyState = {
  loading: false,
  loaded: false,
  questions: [],
  installer: null,
  errors: {},
};

const survey = (state = initialSurveyState, { type, payload }) => {
  switch (type) {
    case 'SURVEY_FETCH_REQUEST':
    case 'SURVEY_UPDATE_REQUEST':
      return {
        ...state,
        loading: true,
      };
    case 'SURVEY_FETCH_SUCCESS':
    case 'SURVEY_UPDATE_SUCCESS':
      return {
        ...state,
        ...payload.survey,
        loading: false,
        loaded: true,
      };
    case 'SURVEY_FETCH_FAILURE':
    case 'SURVEY_UPDATE_FAILURE':
      return {
        ...state,
        loading: false,
        errors: payload.errors,
      };
    default:
      return state;
  }
};

export default combineReducers({
  details,
  documents,
  related,
  survey,
  contractorLineItems: managedPhaseListReducer('contractorLineItems'),
  phaseMerchandiseItems: managedPhaseListReducer('phaseMerchandiseItems'),
  phaseLineItems: managedPhaseListReducer('phaseLineItems'),
  warehouseMerchandiseItems: managedPhaseListReducer('warehouseMerchandiseItems'),
  contractorChargebackItems: managedPhaseListReducer('contractorChargebackItems'),
  purchaseOrders: managedPhaseListReducer('purchaseOrders'),
  laborSheets: managedPhaseListReducer('laborSheets'),
  levelers: managedPhaseListReducer('levelers'),
});

export { getErrors, getLoading, getEvents, getFlags };



// WEBPACK FOOTER //
// ./client/app/reducers/phase.js